import { memo, useState, useCallback, useMemo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import MediaLightbox from '../MediaLightbox';
import GenerationStrip from './GenerationStrip';
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  IconButton,
  Chip,
  Slider,
  Collapse,
} from '@mui/material';
import { Delete, TextFields, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  HANDLES,
  type TextOverlayNodeData,
  type GenerateNodeData,
  type GenerationEntry,
  useCanvasStore,
} from '../useCanvasStore';
import { studioService } from '../../../../services/studio.service';
import { useStudioStore } from '../../stores/studio.store';
import { getAssetSrc } from '../../utils/assetUrl';
import { useNodeTextField } from './useNodeTextField';
import SaveToLibraryButton from '../../components/SaveToLibraryButton';

const FONT_OPTIONS = ['Montserrat', 'Poppins', 'Inter', 'Oswald', 'Merriweather', 'Roboto Condensed'] as const;

const PRESETS: Record<TextOverlayNodeData['preset'], { label: string; defaults: Partial<TextOverlayNodeData> }> = {
  headline: {
    label: 'Headline',
    defaults: { fontSize: 72, fontFamily: 'Montserrat', color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.45)', strokeWidth: 0, y: 20, align: 'center' },
  },
  cta: {
    label: 'Call to Action',
    defaults: { fontSize: 48, fontFamily: 'Montserrat', color: '#ffffff', backgroundColor: '#e53935', strokeWidth: 0, y: 82, align: 'center' },
  },
  caption: {
    label: 'Caption',
    defaults: { fontSize: 32, fontFamily: 'Inter', color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.55)', strokeWidth: 0, y: 90, align: 'center' },
  },
  priceTag: {
    label: 'Price Tag',
    defaults: { fontSize: 56, fontFamily: 'Oswald', color: '#ffeb3b', backgroundColor: 'rgba(0,0,0,0.7)', strokeWidth: 2, strokeColor: '#000000', y: 70, align: 'center' },
  },
  watermark: {
    label: 'Watermark',
    defaults: { fontSize: 24, fontFamily: 'Inter', color: 'rgba(255,255,255,0.35)', backgroundColor: 'transparent', strokeWidth: 0, y: 95, align: 'right' },
  },
  banner: {
    label: 'Banner',
    defaults: { fontSize: 40, fontFamily: 'Poppins', color: '#ffffff', backgroundColor: '#1565c0', strokeWidth: 0, y: 50, align: 'center' },
  },
};

function TextOverlayNode({ id, data }: NodeProps & { data: TextOverlayNodeData }) {
  const updateNodeData = useCanvasStore((s) => s.updateNodeData);
  const removeNode = useCanvasStore((s) => s.removeNode);
  const edges = useCanvasStore((s) => s.edges);
  const nodes = useCanvasStore((s) => s.nodes);
  const addAsset = useStudioStore((s) => s.addAsset);
  const projectId = useStudioStore((s) => s.projectId);
  const { enqueueSnackbar } = useSnackbar();
  const [lightbox, setLightbox] = useState(false);
  const [showStyle, setShowStyle] = useState(false);

  const syncText = useMemo(() => (v: string) => updateNodeData(id, { text: v }), [id, updateNodeData]);
  const textField = useNodeTextField(data.text, syncText);

  // Reactive: source image from connected node
  const sourceEdge = edges.find((e) => e.target === id && e.targetHandle === HANDLES.textSource);
  const sourceNode = sourceEdge ? nodes.find((n) => n.id === sourceEdge.source) : undefined;
  const sourceImage = (sourceNode?.data as GenerateNodeData)?.resultImage;

  const handleSelectGeneration = useCallback(
    (idx: number) => {
      const gens = data.generations || [];
      const gen = gens[idx];
      if (!gen) return;
      updateNodeData(id, {
        selectedGeneration: idx,
        resultImage: gen.image,
        resultMimeType: gen.mimeType,
        assetId: gen.assetId,
        ...(gen.prompt !== undefined && { text: gen.prompt }),
      });
    },
    [id, data.generations, updateNodeData],
  );

  const handleApply = useCallback(async () => {
    if (!data.text.trim() || !sourceImage) return;
    updateNodeData(id, { isGenerating: true });

    try {
      const imageUrl = getAssetSrc(sourceImage, 'image/png');

      const res = await studioService.textOverlay(
        imageUrl,
        data.text,
        {
          x: data.x,
          y: data.y,
          fontSize: data.fontSize,
          fontFamily: data.fontFamily,
          color: data.color,
          backgroundColor: data.backgroundColor,
          strokeColor: data.strokeColor,
          strokeWidth: data.strokeWidth,
          shadow: data.shadow,
          align: data.align,
        },
        projectId || undefined,
        data.preset,
      );

      if (res.image) {
        const newGen: GenerationEntry = {
          image: res.image,
          mimeType: res.mimeType,
          assetId: res.assetId,
          gcsPath: res.gcsPath,
          prompt: data.text,
          timestamp: Date.now(),
        };
        const prevGens = data.generations || [];
        const newGens = [...prevGens, newGen];
        updateNodeData(id, {
          resultImage: res.image,
          resultMimeType: res.mimeType,
          assetId: res.assetId,
          isGenerating: false,
          generations: newGens,
          selectedGeneration: newGens.length - 1,
        });
        addAsset({
          type: 'image',
          data: res.image,
          mimeType: res.mimeType,
          prompt: data.text,
          tool: 'edit',
          serverId: res.assetId,
        });
      }
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: 'error' });
      updateNodeData(id, { isGenerating: false });
    }
  }, [id, data, sourceImage, projectId, updateNodeData, addAsset, enqueueSnackbar]);

  const handlePresetChange = useCallback(
    (preset: TextOverlayNodeData['preset']) => {
      const defaults = PRESETS[preset]?.defaults || {};
      updateNodeData(id, { preset, ...defaults });
    },
    [id, updateNodeData],
  );

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        width: 280,
        overflow: 'hidden',
      }}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id={HANDLES.textSource}
        style={{ background: '#ff9800', width: 10, height: 10 }}
        title="Source Image"
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          bgcolor: '#e65100',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextFields sx={{ fontSize: 16 }} />
          <Typography variant="caption" fontWeight={600}>
            Text Overlay
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => removeNode(id)} sx={{ color: 'inherit' }}>
          <Delete sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Chip
          size="small"
          label={sourceImage ? '✓ Source connected' : '○ Connect source image'}
          color={sourceImage ? 'success' : 'default'}
          variant={sourceImage ? 'filled' : 'outlined'}
          sx={{ fontSize: '0.7rem' }}
        />

        {/* Preset picker */}
        <Select
          size="small"
          value={data.preset}
          onChange={(e) => handlePresetChange(e.target.value as TextOverlayNodeData['preset'])}
          className="nodrag"
          fullWidth
        >
          {Object.entries(PRESETS).map(([key, { label }]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </Select>

        {/* Text input */}
        <TextField
          size="small"
          multiline
          minRows={2}
          maxRows={4}
          placeholder="Enter text to overlay..."
          value={textField.value}
          onChange={textField.onChange}
          onFocus={textField.onFocus}
          onBlur={textField.onBlur}
          fullWidth
          className="nodrag nowheel"
        />

        {/* Style controls toggle */}
        <Button
          size="small"
          variant="text"
          onClick={() => setShowStyle(!showStyle)}
          endIcon={showStyle ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
          sx={{ textTransform: 'none', fontSize: '0.7rem', justifyContent: 'flex-start' }}
        >
          Style options
        </Button>

        <Collapse in={showStyle}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5 }} className="nodrag nowheel">
            {/* Font */}
            <Select
              size="small"
              value={data.fontFamily || 'Montserrat'}
              onChange={(e) => updateNodeData(id, { fontFamily: e.target.value as (typeof FONT_OPTIONS)[number] })}
              fullWidth
            >
              {FONT_OPTIONS.map((font) => (
                <MenuItem key={font} value={font}>{font}</MenuItem>
              ))}
            </Select>

            {/* Font size */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Font size: {data.fontSize}px
              </Typography>
              <Slider
                size="small"
                min={16}
                max={120}
                value={data.fontSize}
                onChange={(_, v) => updateNodeData(id, { fontSize: v as number })}
              />
            </Box>

            {/* Position Y */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Vertical position: {data.y}%
              </Typography>
              <Slider
                size="small"
                min={0}
                max={100}
                value={data.y}
                onChange={(_, v) => updateNodeData(id, { y: v as number })}
              />
            </Box>

            {/* Colors */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Text</Typography>
                <input
                  type="color"
                  value={data.color}
                  onChange={(e) => updateNodeData(id, { color: e.target.value })}
                  style={{ width: 32, height: 24, border: 'none', cursor: 'pointer', display: 'block' }}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Stroke</Typography>
                <input
                  type="color"
                  value={data.strokeColor}
                  onChange={(e) => updateNodeData(id, { strokeColor: e.target.value })}
                  style={{ width: 32, height: 24, border: 'none', cursor: 'pointer', display: 'block' }}
                />
              </Box>
            </Box>

            {/* Stroke width */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Stroke: {data.strokeWidth}px
              </Typography>
              <Slider
                size="small"
                min={0}
                max={8}
                value={data.strokeWidth}
                onChange={(_, v) => updateNodeData(id, { strokeWidth: v as number })}
              />
            </Box>

            {/* Alignment */}
            <Select
              size="small"
              value={data.align || 'center'}
              onChange={(e) => updateNodeData(id, { align: e.target.value as 'left' | 'center' | 'right' })}
              fullWidth
            >
              <MenuItem value="left">Left</MenuItem>
              <MenuItem value="center">Center</MenuItem>
              <MenuItem value="right">Right</MenuItem>
            </Select>
          </Box>
        </Collapse>

        {/* Apply button */}
        <Button
          variant="contained"
          size="small"
          onClick={handleApply}
          disabled={data.isGenerating || !sourceImage || !data.text.trim()}
          startIcon={data.isGenerating ? <CircularProgress size={14} /> : <TextFields sx={{ fontSize: 14 }} />}
          fullWidth
          color="warning"
        >
          {data.isGenerating ? 'Applying…' : 'Apply Text'}
        </Button>

        {/* Result preview */}
        {data.resultImage && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1/1',
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <img
              src={getAssetSrc(data.resultImage, data.resultMimeType || 'image/png')}
              alt="Text overlay result"
              style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
              onClick={() => setLightbox(true)}
            />
            <SaveToLibraryButton assetId={data.assetId} defaultName={data.text} />
          </Box>
        )}
        {data.resultImage && (
          <MediaLightbox
            open={lightbox}
            onClose={() => setLightbox(false)}
            src={getAssetSrc(data.resultImage, data.resultMimeType || 'image/png')}
            type="image"
          />
        )}

        {/* Generation history strip */}
        {data.generations && data.generations.length > 1 && (
          <GenerationStrip
            generations={data.generations}
            selectedIndex={data.selectedGeneration ?? data.generations.length - 1}
            onSelect={handleSelectGeneration}
            type="image"
          />
        )}
      </Box>

      {/* Output handle */}
      {data.resultImage && (
        <Handle
          type="source"
          position={Position.Right}
          id={HANDLES.imageOut}
          style={{ background: '#90caf9', width: 10, height: 10 }}
        />
      )}
    </Box>
  );
}

export default memo(TextOverlayNode);
